<template>
  <Teleport to="#modal">
    <div
      v-if="isOpen"
      class="inga-modal-backdrop"
      :class="{ 'inga-modal-backdrop-dark': isDarkMode }"
      :style="{ '--overlay-z': chartZ }"
      @click="close"
    >
      <div class="inga-modal-content" :class="{ 'inga-modal-content-dark': isDarkMode }" @click.stop>
        <div class="d-flex justify-content-between align-items-center pb-3 border-bottom mb-4" :class="{ 'border-dark-subtle': isDarkMode }">
          <div v-if="share" class="d-flex align-items-center gap-3 min-w-0">
            <div
              class="d-flex align-items-center justify-content-center fw-bold share-logo-wrap"
              :class="{ 'share-logo-fallback': !showShareLogo }"
              :style="showShareLogo
                ? { width: '44px', height: '44px', backgroundColor: 'transparent', flexShrink: 0 }
                : {
                    width: '44px',
                    height: '44px',
                    backgroundColor: hexToRgbA(share.Clr || '#64748b', isDarkMode ? 0.22 : 0.12),
                    color: share.Clr || '#64748b',
                    fontSize: '0.9rem',
                    flexShrink: 0,
                    borderRadius: '50%'
                  }"
            >
              <img
                v-if="showShareLogo"
                :src="shareLogoUrl(share.ISIN)"
                :alt="shareName"
                class="share-logo-img"
                @error="onLogoError"
              />
              <span v-else>{{ shareMonogram(shareName) }}</span>
            </div>
            <div class="min-w-0">
              <h4 class="share-title mb-0 text-truncate" :class="isDarkMode ? 'text-white' : 'text-dark'">
                {{ shareName }}
              </h4>
              <div v-if="share.ISIN" class="share-isin font-monospace text-truncate">{{ share.ISIN }}</div>
            </div>
          </div>
          <div class="d-flex align-items-center gap-2 flex-shrink-0">
            <button
              v-if="share?.ISINID"
              type="button"
              class="forum-bubble-btn"
              :class="{ 'forum-bubble-btn-dark': isDarkMode }"
              :title="'Fórum: ' + shareName"
              @click.stop="openShareForum"
            >
              <fa-icon icon="fa-regular fa-comment-dots" />
            </button>
            <button class="inga-close-btn" :class="{ 'inga-close-btn-dark': isDarkMode }" type="button" @click="close">&times;</button>
          </div>
        </div>

        <div class="inga-modal-body">
          <div v-if="loading" class="text-center py-5" :class="isDarkMode ? 'text-slate-400' : 'text-muted'">
            <div class="spinner-border text-primary mb-3" role="status"></div>
            <p class="mb-0">Stahuji čerstvá data z burzy INGA...</p>
          </div>
          <div v-else-if="chartData" class="w-100">
            <isin-chart :key="share?.ISINID" :chart-data="chartData" />
          </div>
          <div v-else class="text-center py-5 text-danger">
            Nepodařilo se načíst data pro graf.
          </div>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useStore } from "vuex";
import IsinChart from "./Charts/ISINChart.vue";
import { shareLogoUrl, shareMonogram } from "@/helpers/shareLogos";

const store = useStore();
const isDarkMode = computed(() => !!store.state.isDarkMode);
const modal = computed(() => store.state.isinChartModal || {});
const isOpen = computed(() => !!modal.value.isOpen);
const chartZ = computed(() => Number(modal.value.zIndex) || 2050);
const share = computed(() => modal.value.share);
const chartData = computed(() => modal.value.data);
const loading = computed(() => !!modal.value.loading);
const shareName = computed(() => share.value?.Nm || "Akcie");
const logoFailed = ref(false);

const showShareLogo = computed(() => !!(share.value?.ISIN && shareLogoUrl(share.value.ISIN) && !logoFailed.value));

const hexToRgbA = (hex, alpha) => {
  if (!hex) return `rgba(100, 116, 139, ${alpha})`;
  let c;
  if (/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)) {
    c = hex.substring(1).split("");
    if (c.length === 3) c = [c[0], c[0], c[1], c[1], c[2], c[2]];
    c = "0x" + c.join("");
    return `rgba(${(c >> 16) & 255}, ${(c >> 8) & 255}, ${c & 255}, ${alpha})`;
  }
  return `rgba(100, 116, 139, ${alpha})`;
};

const onLogoError = () => {
  logoFailed.value = true;
};

watch(() => share.value?.ISIN, () => {
  logoFailed.value = false;
});

const close = () => store.dispatch("closeIsinChartModal");

const openShareForum = () => {
  if (!share.value?.ISINID) return;
  store.dispatch("openForum", {
    title: shareName.value,
    ISINID: share.value.ISINID,
    ISIN: share.value.ISIN
  });
};

const onKey = (event) => {
  if (event.key !== "Escape" || !isOpen.value) return;
  if (store.state.lightbox?.isOpen) return;
  event.preventDefault();
  event.stopPropagation();
  store.dispatch("closeTopOverlay");
};

onMounted(() => window.addEventListener("keydown", onKey));
onUnmounted(() => window.removeEventListener("keydown", onKey));
</script>

<style scoped>
.inga-modal-backdrop {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(15, 23, 42, 0.6);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--overlay-z, 2050);
  backdrop-filter: blur(4px);
}

.inga-modal-backdrop-dark {
  background-color: rgba(2, 6, 23, 0.78);
}

.inga-modal-content {
  background-color: #ffffff;
  padding: 1.75rem;
  border-radius: 1.25rem;
  width: 92%;
  max-width: 800px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  animation: modalFadeIn 0.2s ease-out;
}

.inga-modal-content-dark {
  background-color: #334155;
  border: 1px solid #94A3B8;
  box-shadow: 0 28px 64px rgba(0, 0, 0, 0.55), 0 0 0 1px rgba(255, 138, 76, 0.2);
}

.share-logo-wrap {
  overflow: hidden;
}

.share-logo-img {
  width: 44px;
  height: 44px;
  object-fit: contain;
  display: block;
}

.share-title {
  font-weight: 800;
  font-size: 1.15rem;
  line-height: 1.2;
}

.share-isin {
  margin-top: 0.15rem;
  font-size: 0.72rem;
  letter-spacing: 0.02em;
  color: #64748B;
}

.inga-modal-content-dark .share-isin {
  color: #94A3B8;
}

.inga-close-btn {
  background: none;
  border: none;
  color: #94a3b8;
  font-size: 1.75rem;
  font-weight: 800;
  cursor: pointer;
  padding: 0;
  line-height: 1;
  text-decoration: none !important;
}

.inga-close-btn:hover {
  color: #334155;
}

.inga-close-btn-dark:hover {
  color: #E2E8F0;
}

.forum-bubble-btn {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1.5px solid #CBD5E1;
  background: transparent;
  color: #64748B;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  flex-shrink: 0;
}

.forum-bubble-btn:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.08);
}

.forum-bubble-btn-dark {
  border-color: #475569;
  color: #94A3B8;
}

.forum-bubble-btn-dark:hover {
  color: #FF8A4C;
  border-color: #FF8A4C;
  background: rgba(255, 138, 76, 0.14);
}

@keyframes modalFadeIn {
  from { opacity: 0; transform: scale(0.97); }
  to { opacity: 1; transform: scale(1); }
}
</style>
